// Auth context: calls /api/me on mount, exposes the current user + roles.
// Components opt into auth via the useAuth() hook; routes opt into role
// gating via the <RequireRole role="..."> wrapper.

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { api, navigateTo, type ApiError } from './api';

export type Role = 'staff' | 'admin';

export interface User {
  oid: string;
  email: string;
  displayName: string;
  roles: Role[];
}

interface AuthState {
  user: User | null;
  loading: boolean;
  refresh: () => Promise<void>;
  login: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const me = await api<User>('/api/me');
      setUser(me);
    } catch (e) {
      const err = e as ApiError;
      if (err.status === 401) {
        setUser(null);
      } else {
        // Network or 500 — leave user as-is, log to console for visibility.
        // eslint-disable-next-line no-console
        console.warn('Failed to load /api/me', err);
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(() => navigateTo('/api/login'), []);
  const logout = useCallback(() => navigateTo('/api/logout'), []);

  const value = useMemo<AuthState>(() => ({ user, loading, refresh, login, logout }), [user, loading, refresh, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

export function hasRole(user: User | null, role: Role): boolean {
  return !!user && user.roles.includes(role);
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <p style={{ padding: 24 }}>Checking session...</p>;
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

export function RequireRole({ role, children }: { role: Role; children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <p style={{ padding: 24 }}>Checking session...</p>;
  if (!user) return <Navigate to="/login" replace />;
  if (!hasRole(user, role)) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}
